import type { UserProfile } from '@disenchantment/types';
import { API_BASE } from '@/config';
import { tryRefreshSession, syncAccessToken } from '@/lib/auth-session';

async function requestOnce<T>(path: string, init: RequestInit = {}, retried401 = false): Promise<T> {
  const token = localStorage.getItem('sb-access-token');
  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string>),
  };
  if (
    init.body !== undefined &&
    init.body !== null &&
    init.body !== '' &&
    !(init.body instanceof FormData)
  ) {
    headers['Content-Type'] = headers['Content-Type'] ?? 'application/json';
  }
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    if (res.status === 401 && !retried401) {
      const refreshed = await tryRefreshSession();
      if (refreshed?.access_token) {
        syncAccessToken(refreshed);
        return requestOnce<T>(path, init, true);
      }
    }
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? `Request failed (${res.status})`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  get<T>(path: string): Promise<T> {
    return requestOnce<T>(path);
  },
  post<T>(path: string, body?: unknown): Promise<T> {
    return requestOnce<T>(path, {
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  },
  patch<T>(path: string, body: unknown): Promise<T> {
    return requestOnce<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
  },
  put<T>(path: string, body: unknown): Promise<T> {
    return requestOnce<T>(path, { method: 'PUT', body: JSON.stringify(body) });
  },
  delete(path: string): Promise<void> {
    return requestOnce<void>(path, { method: 'DELETE' });
  },
  postForm<T>(path: string, formData: FormData): Promise<T> {
    return requestOnce<T>(path, { method: 'POST', body: formData });
  },
  getMe(): Promise<UserProfile> {
    return this.get<UserProfile>('/api/users/me');
  },
};
